import { useState } from 'react';
import { Container, Nav, Navbar, Offcanvas } from 'react-bootstrap';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthState } from '../../auth/AuthContext';
import { isAdmin, isSignedIn } from '../../auth/roles';
import { Paths } from '../../routes/paths';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ColorModeToggle } from './ColorModeToggle';

export function AppNavbar() {
    const auth = useAuthState();
    const { t } = useTranslation();
    const admin = isAdmin(auth);
    const signedIn = isSignedIn(auth);

    // The mobile menu is an offcanvas that folds up from the bottom; we drive
    // its open/closed state so it closes reliably on navigation. (collapseOnSelect
    // doesn't reliably close an offcanvas.)
    const [expanded, setExpanded] = useState(false);
    const close = () => setExpanded(false);

    // Touch devices (phones/tablets) always get the bottom-sheet menu — big
    // phones report desktop-class viewport widths, so a px breakpoint alone
    // misses them. Pointer type is the reliable signal; mouse/desktop keeps the
    // inline nav (collapsing only when the window itself drops below xl).
    const [touch] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
    );

    return (
        <Navbar
            expand={touch ? false : 'xl'}
            data-bs-theme="dark"
            className="app-navbar"
            expanded={expanded}
            onToggle={setExpanded}
        >
            <Container>
                <Navbar.Brand as={Link} to={Paths.home} onClick={close}>
                    <img src="/logo.webp" alt="Dekon IT" />
                </Navbar.Brand>
                {/* On touch / narrow viewports this toggle is restyled (in app.scss)
                    into a floating button pinned to the bottom-right thumb zone, so
                    the trigger sits with the bottom-sheet menu it opens — one-handed
                    reach. It stays inside <Navbar> so the open/close wiring is intact. */}
                <Navbar.Toggle aria-controls="app-navbar-nav" aria-label={t('nav.toggleNavigation')} />
                <Navbar.Offcanvas
                    id="app-navbar-nav"
                    aria-labelledby="app-navbar-title"
                    placement="bottom"
                    data-bs-theme="dark"
                    className="app-navbar__offcanvas"
                    onHide={close}
                >
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title id="app-navbar-title">{t('nav.menu')}</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <Nav className="me-auto">
                            {admin && (
                                <>
                                    <Nav.Link as={NavLink} to={Paths.adminStats} eventKey={Paths.adminStats} onClick={close}>
                                        {t('nav.statistics')}
                                    </Nav.Link>
                                    <Nav.Link as={NavLink} to={Paths.generateToken} eventKey={Paths.generateToken} onClick={close}>
                                        {t('nav.apiToken')}
                                    </Nav.Link>
                                    <Nav.Link as={NavLink} to={Paths.usersOverview} eventKey={Paths.usersOverview} onClick={close}>
                                        {t('nav.users')}
                                    </Nav.Link>
                                    <Nav.Link as={NavLink} to={Paths.updateRole} eventKey={Paths.updateRole} onClick={close}>
                                        {t('nav.role')}
                                    </Nav.Link>
                                    <Nav.Link as={NavLink} to={Paths.smartStore} eventKey={Paths.smartStore} onClick={close}>
                                        {t('nav.mappings')}
                                    </Nav.Link>
                                    <Nav.Link as={NavLink} to={Paths.addApplication} eventKey={Paths.addApplication} onClick={close}>
                                        {t('nav.application')}
                                    </Nav.Link>
                                    <Nav.Link as={NavLink} to={Paths.register} eventKey={Paths.register} onClick={close}>
                                        {t('nav.registration')}
                                    </Nav.Link>
                                </>
                            )}
                        </Nav>
                        <Nav className="align-items-xl-center">
                            {signedIn ? (
                                <>
                                    <Nav.Link as={NavLink} to={Paths.help} eventKey={Paths.help} onClick={close}>
                                        {t('nav.help')}
                                    </Nav.Link>
                                    <Nav.Link as={NavLink} to={Paths.profile} eventKey={Paths.profile} onClick={close}>
                                        {t('nav.profile')}
                                    </Nav.Link>
                                    <Nav.Link as={NavLink} to={Paths.logout} eventKey={Paths.logout} onClick={close}>
                                        {t('nav.logout')}
                                    </Nav.Link>
                                </>
                            ) : (
                                <>
                                    <Nav.Link as={NavLink} to={Paths.adLogin} eventKey={Paths.adLogin} onClick={close}>
                                        {t('nav.adLogin')}
                                    </Nav.Link>
                                    <Nav.Link as={NavLink} to={Paths.login} eventKey={Paths.login} onClick={close}>
                                        {t('nav.login')}
                                    </Nav.Link>
                                </>
                            )}
                            <ColorModeToggle />
                            <LanguageSwitcher />
                        </Nav>
                    </Offcanvas.Body>
                </Navbar.Offcanvas>
            </Container>
        </Navbar>
    );
}
